<template>
    <!-- The Reports index: every report this user can run, by section. A
         report with a settings schema shows that schema's title, description
         and icon, the same as its own page. -->
    <div class="mx-auto w-full max-w-[100rem] sm:px-6 lg:px-8">
        <report-page-header    class="mb-8"
                               title="Reports"
                               description="Leverage SoniCloud's flexible reporting features to understand your facility, satisfy regulators, and improve operations."
        >
            <template v-if="showReviews || eventsUrl" #actions>
                <button v-if="showReviews"
                        type="button"
                        class="inline-flex shrink-0 items-center gap-2 rounded-md border border-blue-600 bg-white px-4 py-2 text-sm font-normal leading-5 text-blue-600 transition-colors duration-150 hover:bg-blue-50 focus:outline-none focus:ring-2 focus:ring-blue-500"
                        @click="showReviewsPanel = true"
                >
                    <tabler-icon    name="circleCheck"
                                    size-class="h-4 w-4"
                    ></tabler-icon>
                    Reviews &amp; Approvals
                </button>

                <a  v-if="eventsUrl"
                    :href="eventsUrl"
                    class="inline-flex shrink-0 items-center gap-2 rounded-md border border-blue-600 bg-white px-4 py-2 text-sm font-normal leading-5 text-blue-600 transition-colors duration-150 hover:bg-blue-50 hover:text-blue-600 hover:no-underline"
                >
                    <tabler-icon    name="chartBar"
                                    size-class="h-4 w-4"
                    ></tabler-icon>
                    All Report Events
                </a>
            </template>
        </report-page-header>

        <section    v-for="section in sections"
                    :key="section.key"
                    class="mb-10"
                    :aria-labelledby="`reports_${section.key}`"
        >
            <h2 :id="`reports_${section.key}`"
                class="m-0 mb-4 p-0 text-left text-sm font-normal uppercase tracking-wide text-gray-500"
            >{{ section.title }}</h2>

            <ul class="m-0 grid list-none grid-cols-1 gap-x-12 gap-y-6 p-0 lg:grid-cols-2">
                <li v-for="report in section.reports"
                    :key="report.key"
                >
                    <a  :href="report.url"
                        class="group flex items-start gap-4 rounded-md p-1 hover:no-underline focus:outline-none focus-visible:ring-2 focus-visible:ring-blue-500"
                    >
                        <!-- Light when the report is on for this site, dark when not. -->
                        <span   class="flex h-9 w-9 shrink-0 items-center justify-center rounded-md text-white"
                                :class="report.enabled ? 'bg-blue-500' : 'bg-blue-900'"
                                aria-hidden="true"
                        >
                            <svg    xmlns="http://www.w3.org/2000/svg"
                                    width="20"
                                    height="20"
                                    viewBox="0 0 24 24"
                                    fill="none"
                                    stroke="currentColor"
                                    stroke-width="2"
                                    stroke-linecap="round"
                                    stroke-linejoin="round"
                                    v-html="iconFor(report)"
                            ></svg>
                        </span>

                        <span class="min-w-0">
                            <span class="block text-xl leading-7 text-blue-600 group-hover:text-blue-800 group-hover:underline">{{ titleFor(report) }}</span>
                            <span class="block text-sm text-gray-600">{{ descriptionFor(report) }}</span>
                        </span>
                    </a>
                </li>
            </ul>
        </section>

        <slide-over-panel   v-if="showReviews"
                            v-model:show="showReviewsPanel"
        >
            <template #title>
                Reviews &amp; Approvals
            </template>

            <template #content>
                <p  class="m-0 mb-4 text-sm leading-6 text-gray-600">
                    The latest reports you or your groups have reviewed or approved.
                </p>

                <alert-toast    v-model:show="showReviewsError"
                                type="error"
                                title="Error"
                                :duration="0"
                >Reviews and approvals could not be loaded. Please try again.</alert-toast>

                <div    v-if="reviewsLoading && reviews.length === 0"
                        class="flex items-center justify-center gap-2 py-10 text-sm text-gray-500"
                >
                    <tabler-icon    name="loader"
                                    size-class="h-4 w-4 animate-spin"
                    ></tabler-icon>
                    Loading
                </div>

                <div    v-else-if="reviewsLoaded && reviews.length === 0"
                        class="flex flex-col items-center gap-2 rounded-lg border border-gray-200 bg-white px-4 py-10 text-center shadow-sm"
                >
                    <tabler-icon    name="inbox"
                                    size-class="h-8 w-8"
                                    class="text-gray-400"
                    ></tabler-icon>
                    <p class="m-0 text-base font-medium text-gray-900">No reviews or approvals yet</p>
                </div>

                <!-- The same banded rows as the report settings. -->
                <ul v-else-if="reviews.length > 0"
                    class="report-settings-card-rows m-0 list-none p-0"
                >
                    <li v-for="review in reviews"
                        :key="review.id"
                        class="report-settings-band rounded-md"
                    >
                        <div class="flex items-center gap-3 px-2 py-3">
                            <span   class="flex h-8 w-8 shrink-0 items-center justify-center rounded-md bg-blue-500 text-white"
                                    aria-hidden="true"
                            >
                                <svg    xmlns="http://www.w3.org/2000/svg"
                                        width="18"
                                        height="18"
                                        viewBox="0 0 24 24"
                                        fill="none"
                                        stroke="currentColor"
                                        stroke-width="2"
                                        stroke-linecap="round"
                                        stroke-linejoin="round"
                                        v-html="reviewSchema(review)?.icon ?? DEFAULT_REPORT_ICON"
                                ></svg>
                            </span>

                            <span class="min-w-0 flex-1">
                                <span class="flex flex-wrap items-center gap-2">
                                    <span class="text-sm font-medium text-gray-900">{{ reviewSchema(review)?.title ?? review.reportName }}</span>
                                    <span   class="rounded-full px-2 py-0.5 text-xs font-medium text-white"
                                            :class="review.approval ? 'bg-green-500' : 'bg-blue-600'"
                                    >{{ review.approval ? 'Approved' : 'Reviewed' }}</span>
                                </span>
                                <span class="mt-0.5 block text-sm text-gray-500">{{ review.user }} · {{ review.when }}</span>
                            </span>

                            <a  :href="review.url"
                                class="inline-flex shrink-0 items-center gap-1 whitespace-nowrap text-sm text-blue-600 hover:text-blue-800"
                            >
                                View Report
                                <tabler-icon    name="chevronRight"
                                                size-class="h-4 w-4"
                                ></tabler-icon>
                            </a>
                        </div>
                    </li>
                </ul>

                <!-- Pages of ten, newest first. -->
                <nav    v-if="reviewsMeta.lastPage > 1"
                        class="mt-4 flex items-center justify-between gap-3"
                        aria-label="Reviews and approvals pages"
                >
                    <span class="text-sm text-gray-500">{{ reviewsRange }}</span>

                    <span class="flex items-center gap-2">
                        <button type="button"
                                :disabled="reviewsLoading || reviewsMeta.page <= 1"
                                class="inline-flex items-center gap-1 rounded-md border border-gray-300 bg-white px-3 py-1.5 text-sm text-gray-700 hover:bg-gray-50 disabled:cursor-not-allowed disabled:opacity-50"
                                @click="loadReviews(reviewsMeta.page - 1)"
                        >
                            <tabler-icon    name="arrowLeft"
                                            size-class="h-4 w-4"
                            ></tabler-icon>
                            Previous
                        </button>
                        <button type="button"
                                :disabled="reviewsLoading || reviewsMeta.page >= reviewsMeta.lastPage"
                                class="inline-flex items-center gap-1 rounded-md border border-gray-300 bg-white px-3 py-1.5 text-sm text-gray-700 hover:bg-gray-50 disabled:cursor-not-allowed disabled:opacity-50"
                                @click="loadReviews(reviewsMeta.page + 1)"
                        >
                            Next
                            <tabler-icon    name="arrowRight"
                                            size-class="h-4 w-4"
                            ></tabler-icon>
                        </button>
                    </span>
                </nav>

                <a  v-if="eventsUrl"
                    :href="eventsUrl"
                    class="mt-4 inline-flex items-center gap-1 text-sm text-blue-600 hover:text-blue-800"
                >
                    View all report events
                    <tabler-icon    name="chevronRight"
                                    size-class="h-4 w-4"
                    ></tabler-icon>
                </a>
            </template>
        </slide-over-panel>
    </div>
</template>

<script>
import axios from "axios"

import TablerIcon from "@/components/elements/TablerIcon.vue"
import AlertToast from "@/components/elements/AlertToast.vue"
import SlideOverPanel from "@/components/elements/SlideOverPanel.vue"
import ReportPageHeader from "@/components/GroupReports/settings/ReportPageHeader.vue"
import {REPORT_SCHEMAS} from "@/components/GroupReports/settings/schemas/index.js"
import {DEFAULT_REPORT_ICON} from "@/components/GroupReports/settings/reportIcons.js"

export default {
    name: "ReportsIndex",
    components: {TablerIcon, AlertToast, SlideOverPanel, ReportPageHeader},
    props: {
        // [{key, title, reports: [{key, schema, title, description, url, enabled}]}]
        // (App\Reports\ReportIndex).
        sections: {
            type: Array,
            required: true,
        },
        // Pages of the reviews and approvals (ReportsController@getReviews).
        reviewsUrl: {
            type: String,
            default: null,
        },
        showReviews: {
            type: Boolean,
            default: false,
        },
        // Report History; null on sites without group reporting.
        eventsUrl: {
            type: String,
            default: null,
        },
    },
    data() {
        return {
            showReviewsPanel: false,
            reviews: [],
            reviewsMeta: {page: 1, perPage: 10, total: 0, lastPage: 1},
            reviewsLoading: false,
            reviewsLoaded: false,
            showReviewsError: false,
            DEFAULT_REPORT_ICON,
        }
    },
    computed: {
        // "11–20 of 37"
        reviewsRange() {
            const {page, perPage, total} = this.reviewsMeta
            const first = ((page - 1) * perPage) + 1
            return `${first}–${Math.min(page * perPage, total)} of ${total}`
        },
    },
    watch: {
        // Loaded the first time the panel opens, not with the page.
        showReviewsPanel(open) {
            if (open && ! this.reviewsLoaded) {
                this.loadReviews(1)
            }
        },
    },
    methods: {
        loadReviews(page) {
            if (this.reviewsLoading || ! this.reviewsUrl) {
                return
            }
            this.reviewsLoading = true
            this.showReviewsError = false

            axios.get(this.reviewsUrl, {params: {page}})
                .then(({data}) => {
                    this.reviews = data.data ?? []
                    this.reviewsMeta = data.meta ?? this.reviewsMeta
                    this.reviewsLoaded = true
                })
                .catch(() => {
                    this.showReviewsError = true
                })
                .finally(() => {
                    this.reviewsLoading = false
                })
        },
        schemaFor(report) {
            return report.schema ? REPORT_SCHEMAS[report.key] : null
        },
        titleFor(report) {
            return this.schemaFor(report)?.title ?? report.title
        },
        descriptionFor(report) {
            return this.schemaFor(report)?.description ?? report.description
        },
        // Report history names a report by its type, e.g.
        // 'weekly-device-summary-report'; schemas may drop the suffix.
        reviewSchema(review) {
            const key = review.reportKey ?? ''
            return REPORT_SCHEMAS[key] ?? REPORT_SCHEMAS[key.replace(/-report$/, '')] ?? null
        },
        iconFor(report) {
            return this.schemaFor(report)?.icon ?? DEFAULT_REPORT_ICON
        },
    },
}
</script>
